import { useEffect, useState } from 'react'
import { Copy, Check, Trash2, Link2, Clock, Infinity as InfinityIcon } from 'lucide-react'
import Modal from '../ui/Modal.jsx'
import Button from '../ui/Button.jsx'
import Input, { Field } from '../ui/Input.jsx'
import ConfirmDialog from '../ui/ConfirmDialog.jsx'
import { useToast } from '../ui/Toast.jsx'
import { listShareLinks, createShareLink, revokeShareLink } from '../../lib/api.js'

const QUICK_EXPIRY_DAYS = [7, 14, 30, 60, 90]
function addDays(days) {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function isExpired(link) {
  return !!link.expires_at && new Date(link.expires_at) < new Date()
}
function isRevoked(link) {
  return !!link.revoked_at
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      className="w-8 h-8 shrink-0 rounded-md bg-panel2 border border-line hover:border-sky/40 flex items-center justify-center transition-colors"
      title="Copy link"
    >
      {copied ? <Check size={13} className="text-ok" /> : <Copy size={13} />}
    </button>
  )
}

/** Time-limited, revocable share links — distinct from Publish. Works on a
 *  draft project too (a private preview link), not just published books.
 *  Each link is its own random token, independently expirable/revocable. */
export default function ShareLinkModal({ open, onClose, projectId }) {
  const [links, setLinks] = useState(null)
  const [label, setLabel] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [creating, setCreating] = useState(false)
  const [confirmRevokeId, setConfirmRevokeId] = useState(null)
  const toast = useToast()

  async function refresh() {
    setLinks(await listShareLinks(projectId))
  }
  useEffect(() => { if (open) refresh() }, [open, projectId])

  async function handleCreate(e) {
    e.preventDefault()
    setCreating(true)
    try {
      const link = await createShareLink(projectId, {
        label: label.trim() || null,
        expiresAt: expiresAt ? new Date(expiresAt + 'T23:59:59').toISOString() : null
      })
      const copied = await navigator.clipboard?.writeText(link.url).then(() => true).catch(() => false)
      toast(copied ? 'Share link created and copied to clipboard' : 'Share link created', 'success')
      setLabel(''); setExpiresAt('')
      refresh()
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(id) {
    try {
      await revokeShareLink(id)
      toast('Link revoked', 'success')
      refresh()
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Share with a client" width={480}>
      <p className="text-muted text-[13px] mb-4 leading-relaxed">
        Anyone with the link can view this book — no account needed. Set an expiry so access ends automatically,
        or leave it open and revoke it manually whenever you like. Works even before you Publish.
      </p>

      <form onSubmit={handleCreate} className="mb-5">
        <Field label="Label (optional)" hint="e.g. the client's name, so you can tell links apart later.">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. John Doe — Company XYZ" />
        </Field>
        <Field label="Expires on (optional)" hint="Leave blank for a link that never expires.">
          <div className="flex gap-1.5 mb-2 flex-wrap">
            {QUICK_EXPIRY_DAYS.map((days) => {
              const date = addDays(days)
              const active = expiresAt === date
              return (
                <button
                  key={days}
                  type="button"
                  onClick={() => setExpiresAt(active ? '' : date)}
                  className={`px-2.5 py-1 rounded-full text-[12px] font-medium border transition-colors duration-150 ${
                    active ? 'border-blue/40 text-sky bg-blue/10' : 'border-line text-muted-2 hover:text-muted'
                  }`}
                >
                  {days}d
                </button>
              )
            })}
          </div>
          <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
        </Field>
        <Button type="submit" icon={Link2} className="w-full" loading={creating}>Create share link</Button>
      </form>

      <div className="border-t border-line pt-4">
        <div className="text-[11px] font-semibold tracking-wide uppercase text-muted mb-3">Active links</div>
        {links === null ? (
          <div className="text-muted-2 text-[13px]">Loading…</div>
        ) : links.length === 0 ? (
          <div className="text-muted-2 text-[13px]">No share links yet.</div>
        ) : (
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {links.map((link) => {
              const expired = isExpired(link)
              const revoked = isRevoked(link)
              const dead = expired || revoked
              const url = `${import.meta.env.VITE_VIEWER_BASE_URL || 'https://flipstudio-viewer.pages.dev'}/s/${link.token}`
              return (
                <div key={link.id} className={`flex items-center gap-2 p-2.5 rounded-md border ${dead ? 'border-line opacity-50' : 'border-line'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{link.label || 'Untitled link'}</div>
                    <div className="text-[11px] text-muted-2 flex items-center gap-1">
                      {revoked ? 'Revoked' : expired ? 'Expired' : link.expires_at ? (
                        <><Clock size={11} /> Expires {new Date(link.expires_at).toLocaleDateString()}</>
                      ) : (
                        <><InfinityIcon size={11} /> Never expires</>
                      )}
                    </div>
                  </div>
                  {!dead && <CopyButton text={url} />}
                  {!dead && (
                    <button
                      onClick={() => setConfirmRevokeId(link.id)}
                      className="w-8 h-8 shrink-0 rounded-md bg-panel2 border border-line hover:border-danger/40 hover:text-danger flex items-center justify-center transition-colors"
                      title="Revoke"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmRevokeId !== null}
        onClose={() => setConfirmRevokeId(null)}
        onConfirm={() => handleRevoke(confirmRevokeId)}
        title="Revoke this link?"
        description="Anyone still using this link will immediately lose access. This can't be undone."
        confirmLabel="Revoke"
      />
    </Modal>
  )
}
